import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuBanknote, LuCalendarDays, LuCircleCheck, LuClock, LuFileSpreadsheet, LuRotateCcw, LuTrash2, LuUndo2, LuUsers, LuWallet } from 'react-icons/lu';
import DataCardList from '../../components/data/DataCardList';
import DataTable from '../../components/data/DataTable';
import DataToolbar from '../../components/data/DataToolbar';
import FilterSheet from '../../components/data/FilterSheet';
import PageHero from '../../components/data/PageHero';
import StatTiles from '../../components/data/StatTiles';
import TablePagination from '../../components/data/TablePagination';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import useElementWidth from '../../hooks/useElementWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import usePagedList from '../../hooks/usePagedList';
import useTableColumns from '../../hooks/useTableColumns';
import useViewportFit from '../../hooks/useViewportFit';
import AddSalaryModal from './EmployeeFinanceManagement/AddSalaryModal';
import BulkPaymentModal from './salaryPayment/BulkPaymentModal';
import useSalaryPayments from './salaryPayment/useSalaryPayments';
import useSalaryPaymentText from './salaryPayment/salaryPaymentText';
import {
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  MIN_COLUMN_WIDTH,
  PAID_VALUES,
  PAYMENT_COLUMNS,
  PINNED_COLUMNS,
  getCurrentYearMonth,
  getFilterState,
} from './salaryPayment/salaryPaymentColumns';
import SalaryPaymentCell, { SalaryPaymentCardRow, useSalaryCellText } from './salaryPayment/parts/SalaryPaymentCell';
import SalaryPaymentDetailPane from './salaryPayment/parts/SalaryPaymentDetailPane';

const COLUMNS_STORAGE_KEY = 'salaryPayment.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Maaş Ödemeleri (Tailwind), built like the other data pages: a payment
 * month, tiles (paid / unpaid filter, amount still to pay), a table card that
 * reaches at most the bottom of the screen, the selected salary's panel
 * (pay / take back, edit, delete, payment history) and the bulk payment
 * preview. The data logic lives in useSalaryPayments.
 */
const SalaryPaymentPage = () => {
  const {
    rows, isLoading, filteredRows, stats, selectedMonth, setSelectedMonth, filters, handleFilter, clearFilters,
    searchTerm, setSearchTerm, selectedSalary, setSelectedId, buildPaymentPreview, handleBulkPayment,
    handleSinglePayment, handleReversePayment, handleDeleteSalary, downloadExcel, refresh,
  } = useSalaryPayments();
  const { language, locale, label, formatMoney, formatPeriod } = useSalaryPaymentText();
  const cellText = useSalaryCellText();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: PAYMENT_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  const maxCardHeight = !compactList && fitHeight ? fitHeight : undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type: 'reverse' | 'delete', salary }
  const [busy, setBusy] = useState(false);
  const hasLoaded = rows.length > 0 || !isLoading;
  const isCurrentMonth = selectedMonth === getCurrentYearMonth();
  const period = formatPeriod(selectedMonth);

  const resetKey = JSON.stringify([filters, searchTerm, selectedMonth]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredRows, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  useEffect(() => {
    if (!selectedSalary) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedId(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedSalary, showFilterSheet, setSelectedId]);

  const columnLabel = useCallback((key) => label(`columns.${key}`), [label]);
  const paidOptions = useMemo(() => [{ value: 'true', label: label('status.paid') }, { value: 'false', label: label('status.unpaid') }], [label]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: { type: PAYMENT_COLUMNS[key].type, options: key === 'isCurrentMonthPaid' ? paidOptions : undefined },
  })), [columns.visibleKeys, columnLabel, paidOptions]);

  // Ödendi / Ödenmedi seçimi araç çubuğundaki düğmede görünür
  const filterChips = Object.keys(filters)
    .filter((key) => key !== 'isCurrentMonthPaid' && PAYMENT_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: String(filters[key]), state: 'applied' }));

  const paidValue = filters.isCurrentMonthPaid || '';
  const isFilterActive = Object.values(filters).some(Boolean) || Boolean(searchTerm);

  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedSalary ? filteredRows.findIndex((salary) => salary.id === selectedSalary.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredRows[selectedIndex + offset];
    if (!sibling) return;
    setSelectedId(sibling.id);
    reveal(sibling.id);
  };

  const openBulkPayment = () => {
    const preview = buildPaymentPreview();
    if (preview) setBulkPreview(preview);
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    const ok = confirm.type === 'reverse' ? await handleReversePayment(confirm.salary) : await handleDeleteSalary(confirm.salary);
    setBusy(false);
    if (ok) setConfirm(null);
  };

  const setPaid = (value) => handleFilter('isCurrentMonthPaid', value);
  const shareOf = (count) => (stats.total > 0 ? Math.min(1, count / stats.total) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const tiles = [
    { key: 'total', label: label('stats.total'), shortLabel: label('stats.totalShort'), value: stats.total, icon: LuUsers, gradient: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30', onClick: clearFilters, active: !isFilterActive },
    { key: 'paid', label: label('stats.paid'), shortLabel: label('stats.paidShort'), value: stats.paid, icon: LuCircleCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(stats.paid), shareTitle: shareTitle(stats.paid), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500', onClick: () => setPaid(paidValue === 'true' ? '' : 'true'), active: paidValue === 'true' },
    { key: 'unpaid', label: label('stats.unpaid'), shortLabel: label('stats.unpaidShort'), value: stats.unpaid, icon: LuClock, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30', onClick: () => setPaid(paidValue === 'false' ? '' : 'false'), active: paidValue === 'false' },
    { key: 'toPay', label: label('stats.toPay'), shortLabel: label('stats.toPayShort'), value: stats.toPay, format: (value) => formatMoney(value).replace(' ₺', ''), suffix: '₺', icon: LuWallet, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30' },
  ];

  const monthPicker = (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
      <label className="tw-relative tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-teal-500/40 tw-bg-teal-500/[0.08] tw-pl-3 tw-pr-2 tw-text-xs tw-font-semibold tw-text-teal-800 dark:tw-border-teal-400/30 dark:tw-text-teal-200">
        <LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
        <span className="tw-sr-only">{label('toolbar.month')}</span>
        <input
          type="month"
          value={selectedMonth}
          onChange={(event) => event.target.value && setSelectedMonth(event.target.value)}
          className="tw-h-full tw-w-[8.5rem] tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-xs tw-font-semibold tw-text-teal-900 tw-shadow-none tw-outline-none dark:tw-text-teal-100 dark:[color-scheme:dark]"
        />
      </label>
      {!isCurrentMonth && (
        <button
          type="button"
          onClick={() => setSelectedMonth(getCurrentYearMonth())}
          className="tw-inline-flex tw-h-8 tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-bg-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/40 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-300"
        >
          <LuRotateCcw aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
          {label('toolbar.currentMonth')}
        </button>
      )}
    </span>
  );

  const paneProps = {
    salary: selectedSalary,
    month: selectedMonth,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredRows.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredRows.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedId(null),
    onPay: () => handleSinglePayment(selectedSalary),
    onReverse: () => setConfirm({ type: 'reverse', salary: selectedSalary }),
    onEdit: () => setEditing(selectedSalary),
    onDelete: () => setConfirm({ type: 'delete', salary: selectedSalary }),
  };

  const confirmName = confirm ? `${confirm.salary.employeeName} ${confirm.salary.employeeSurname}`.trim() : '';

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <PageHero
          icon={LuBanknote}
          gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30"
          title={label('page.title')}
          subtitle={label('page.subtitle', { period })}
          actionsClassName="tw-grid tw-grid-cols-[auto_minmax(0,1fr)] tw-items-center tw-gap-2 sm:tw-flex sm:tw-flex-wrap lg:tw-justify-end"
        >
          <Button onClick={downloadExcel} aria-label={label('actions.excel')} title={label('actions.excelHint')} className="max-sm:tw-w-10 max-sm:tw-px-0">
            <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
            <span className="tw-hidden sm:tw-inline">{label('actions.excel')}</span>
          </Button>
          <Button variant="primary" onClick={openBulkPayment} disabled={stats.unpaid === 0} title={label('actions.bulkHint', { period })} className="max-sm:tw-w-full">
            <LuBanknote aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('actions.bulk', { count: stats.unpaid })}
          </Button>
        </PageHero>

        <StatTiles tiles={tiles} ready={hasLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              status={{
                options: PAID_VALUES.map((value) => ({ value, label: label(`toolbar.paid_${value || 'all'}`) })),
                value: paidValue,
                onChange: setPaid,
                layoutId: 'salary-payment-status',
                label: label('toolbar.status'),
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
              extra={monthPicker}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(salary) => <SalaryPaymentCardRow salary={salary} />}
                selectedId={selectedSalary?.id}
                onSelect={(salary) => setSelectedId(salary.id)}
                isLoading={isLoading}
                isBusy={false}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(salary, key) => <SalaryPaymentCell columnKey={key} salary={salary} />}
                getCellTitle={(salary, key) => cellText(salary, key) || undefined}
                selectedId={selectedSalary?.id}
                onSelect={(salary) => setSelectedId(salary.id)}
                isLoading={isLoading}
                isBusy={false}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredRows.length}
              rangeOf={label('table.rangeOf', { total: filteredRows.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: stats.total.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={false}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <SalaryPaymentDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <SalaryPaymentDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key), type: PAYMENT_COLUMNS[key].type, options: key === 'isCurrentMonthPaid' ? paidOptions : undefined }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredRows.length}
      />

      {bulkPreview && (
        <BulkPaymentModal
          initialData={bulkPreview}
          period={period}
          onClose={() => setBulkPreview(null)}
          onConfirm={handleBulkPayment}
        />
      )}

      {editing && (
        <AddSalaryModal
          onClose={() => setEditing(null)}
          onSalaryUpdated={refresh}
          employeeId={editing.employeeId}
          editingSalary={editing}
          employeeName={`${editing.employeeName} ${editing.employeeSurname}`.trim()}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        title={confirm?.type === 'delete' ? label('confirm.deleteTitle') : label('confirm.reverseTitle')}
        message={confirm?.type === 'delete'
          ? label('confirm.deleteMessage', { name: confirmName })
          : label('confirm.reverseMessage', { name: confirmName, period })}
        confirmLabel={confirm?.type === 'delete' ? label('panel.delete') : label('panel.reverse')}
        cancelLabel={label('common.cancel')}
        tone={confirm?.type === 'delete' ? 'danger' : 'warning'}
        icon={confirm?.type === 'delete' ? LuTrash2 : LuUndo2}
        busy={busy}
        onConfirm={runConfirm}
        onCancel={() => !busy && setConfirm(null)}
      />
    </>
  );
};

export default SalaryPaymentPage;
